import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import GameWithClosingNow from "../components/games/GameWithClosingNow";
import { useAuth } from "../context/AuthContext";
import { db, functions } from "../firebase";
import { gameGuessErrorMessage } from "../utils/gameGuessError";
import "./NumberDrop.css";

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
const SHARE_GLYPH = { correct: "🟧", present: "🟨", absent: "⬛" };

function emptyBoard() {
  return { rows: [], current: "", status: "playing", error: "", digitSum: null };
}

function todayKey() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function puzzleNumber(dateStr) {
  const [year, month, day] = dateStr.split("-").map(Number);
  const start = Date.UTC(year, 0, 0);
  const current = Date.UTC(year, month - 1, day);
  return Math.round((current - start) / 86400000);
}

function firstName(displayName) {
  const name = String(displayName || "").trim();
  if (!name) return "Creator";
  return name.split(/\s+/)[0];
}

function initials(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function digitLabel(grid) {
  return grid === 5 ? "5-digit" : "4-digit";
}

function pointsForResult(solved, guessCount) {
  if (!solved) return 0;
  return Math.max(0, 7 - Number(guessCount || 0));
}

function colorRows(score, grid) {
  if (Array.isArray(score?.tileRows) && score.tileRows.length) {
    return score.tileRows.map((row) => {
      if (Array.isArray(row)) return row;
      if (Array.isArray(row?.tiles)) return row.tiles;
      if (typeof row === "string") return row.split("|").filter(Boolean);
      return [];
    }).filter((row) => row.length);
  }
  const count = Number(score?.guessCount || 0);
  if (!count) return [];
  return Array.from({ length: Math.min(count, 6) }, () => Array.from({ length: grid }, () => "unknown"));
}

function cardCopy(displayName, score, grid) {
  const name = firstName(displayName);
  const label = digitLabel(grid);
  const played = score && Number(score.grid) === grid && Number(score.guessCount) > 0;
  if (!played) {
    return {
      title: `${name} hasn't played the ${label}`,
      subtitle: "Be first on this board",
      rows: [],
    };
  }
  if (score.solved === true) {
    const beatIn = Number(score.guessCount) - 1;
    return {
      title: `${name} got the ${label} in ${score.guessCount}`,
      subtitle: beatIn >= 1
        ? `Solve it in ${beatIn} to take the lead on ${name}'s ${label} board`
        : `${name} already finished in 1`,
      rows: colorRows(score, grid),
    };
  }
  return {
    title: `${name} didn't solve the ${label}`,
    subtitle: "A finish puts you ahead on this board",
    rows: colorRows(score, grid),
  };
}

function comparisonLine(displayName, score, grid, solved, guessCount) {
  const name = firstName(displayName);
  const label = digitLabel(grid);
  const played = score && Number(score.grid) === grid && Number(score.guessCount) > 0;
  if (!played) return `${name} hasn't played the ${label} yet.`;
  if (score.solved === true && solved) {
    if (Number(guessCount) === Number(score.guessCount)) {
      return `You tied ${name} — both in ${guessCount}.`;
    }
    return `${name} got it in ${score.guessCount}. You got it in ${guessCount}.`;
  }
  if (score.solved === true) return `${name} got the ${label} in ${score.guessCount}.`;
  if (solved) return `You got it. ${name} didn't solve the ${label}.`;
  return `${name} didn't solve the ${label} either.`;
}

function shareText(puzzleNo, grid, rows) {
  const body = rows
    .map((row) => (row.tiles || []).map((tile) => SHARE_GLYPH[tile] || "⬜").join(""))
    .join("\n");
  return `Number Drop · No. ${puzzleNo}\n${grid} digits\n${body}`;
}

function shareImage(rows) {
  const tile = 28;
  const gap = 6;
  const width = rows.reduce((max, row) => Math.max(max, (row.tiles || []).length), 0);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, width) * (tile + gap) + gap;
  canvas.height = Math.max(1, rows.length) * (tile + gap) + gap;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FAFAFA";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const fill = { correct: "#C4790A", present: "#F2D27A", absent: "#6B7280" };
  rows.forEach((row, rowIndex) => {
    (row.tiles || []).forEach((tileState, index) => {
      ctx.fillStyle = fill[tileState] || "#E8ECF2";
      const x = gap + index * (tile + gap);
      const y = gap + rowIndex * (tile + gap);
      ctx.beginPath();
      ctx.roundRect(x, y, tile, tile, 6);
      ctx.fill();
    });
  });
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export default function NumberDrop({ gameSwitch = null }) {
  const { currentUser, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const date = useMemo(() => todayKey(), []);
  const puzzleNo = puzzleNumber(date);
  const [grid, setGrid] = useState(() => (Number(searchParams.get("grid")) === 5 ? 5 : 4));
  const [boards, setBoards] = useState({ 4: emptyBoard(), 5: emptyBoard() });
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [shareNote, setShareNote] = useState("");
  const [creator, setCreator] = useState(null);
  const [creatorScore, setCreatorScore] = useState(null);
  const [creatorNote, setCreatorNote] = useState("");

  const gridRef = useRef(grid);
  const boardsRef = useRef(boards);
  const busyRef = useRef(false);
  const sheetRef = useRef(sheet);
  const creatorIdRef = useRef("");
  gridRef.current = grid;
  boardsRef.current = boards;
  sheetRef.current = sheet;

  const board = boards[grid];

  const reloadCreatorScore = useCallback(async (creatorId, size = grid) => {
    if (!creatorId) {
      setCreatorScore(null);
      return;
    }
    try {
      const snap = await getDoc(doc(db, "numberDrop_scores", `${date}_${creatorId}_${creatorId}_${size}`));
      setCreatorScore(snap.exists() ? snap.data() : null);
      setCreatorNote("");
    } catch (err) {
      setCreatorScore(null);
      setCreatorNote(err?.code === "permission-denied"
        ? "This board is locked to the creator's own score."
        : "This board couldn't be loaded.");
    }
  }, [date, grid]);

  useEffect(() => {
    let alive = true;
    const param = (searchParams.get("creator") || "").trim();

    async function resolveCreatorId() {
      if (param) {
        const direct = await getDoc(doc(db, "users", param));
        if (direct.exists()) return direct.id;
        const handle = await getDoc(doc(db, "usernames", param.toLowerCase()));
        const uid = handle.exists() ? handle.data()?.uid : "";
        if (uid) return uid;
      }
      if (userProfile?.isCreator && currentUser?.uid) return currentUser.uid;
      const followed = Array.isArray(userProfile?.following) ? userProfile.following.filter(Boolean) : [];
      if (followed.length) return followed[0];
      if (!currentUser?.uid) return "";
      const follows = await getDocs(query(
        collection(db, "follows"),
        where("followerId", "==", currentUser.uid),
        limit(1)
      ));
      return follows.docs[0]?.data()?.creatorId || "";
    }

    (async () => {
      try {
        const creatorId = await resolveCreatorId();
        if (!alive) return;
        creatorIdRef.current = creatorId || "";
        if (!creatorId) {
          setCreator(null);
          setCreatorScore(null);
          setCreatorNote("Open a creator's board to play.");
          return;
        }
        const profile = await getDoc(doc(db, "users", creatorId));
        if (!alive) return;
        setCreator({
          id: creatorId,
          displayName: profile.exists() ? (profile.data()?.displayName || "Creator") : "Creator",
        });
        await reloadCreatorScore(creatorId);
      } catch (err) {
        if (!alive) return;
        setCreatorNote(err?.message || "This board couldn't be loaded.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentUser, userProfile, searchParams, reloadCreatorScore]);

  const setBoardError = useCallback((size, error) => {
    setBoards((prev) => ({ ...prev, [size]: { ...prev[size], error } }));
  }, []);

  const submitCurrent = useCallback(async () => {
    const size = gridRef.current;
    const current = boardsRef.current[size];
    if (!current || current.status !== "playing" || busyRef.current || sheetRef.current) return;
    if (current.current.length !== size) {
      setShake(false);
      requestAnimationFrame(() => setShake(true));
      return;
    }
    if (!currentUser?.uid) {
      setBoardError(size, "Sign in to play.");
      return;
    }
    if (!creatorIdRef.current) {
      setBoardError(size, "Open a creator's board to play.");
      return;
    }

    busyRef.current = true;
    setBusy(true);
    try {
      const submit = httpsCallable(functions, "submitNumberDropGuess");
      const res = await submit({
        date,
        creatorId: creatorIdRef.current,
        grid: size,
        guess: current.current,
      });
      const data = res.data || {};
      const tiles = Array.isArray(data.tiles) ? data.tiles : [];
      if (tiles.length !== size) {
        setBoardError(size, "That guess didn't go through. Try again.");
        return;
      }
      const solved = tiles.length > 0 && tiles.every((tile) => tile === "correct");
      const ended = data.ended === true || solved;
      const digitSum = Number.isFinite(Number(data.digitSum)) ? Number(data.digitSum) : null;
      setBoards((prev) => {
        const latest = prev[size];
        return {
          ...prev,
          [size]: {
            ...latest,
            rows: [...latest.rows, { guess: latest.current, tiles }],
            current: "",
            status: solved ? "solved" : ended ? "failed" : "playing",
            error: "",
            digitSum: digitSum == null ? latest.digitSum : digitSum,
          },
        };
      });
      if (ended) {
        const guessCount = boardsRef.current[size].rows.length + 1;
        setSheet({ grid: size, solved, guessCount });
        reloadCreatorScore(creatorIdRef.current);
      }
    } catch (err) {
      setBoardError(size, gameGuessErrorMessage(err, "number"));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [currentUser, date, reloadCreatorScore, setBoardError]);

  const typeDigit = useCallback((digit) => {
    const size = gridRef.current;
    if (busyRef.current || sheetRef.current) return;
    setBoards((prev) => {
      const current = prev[size];
      if (!current || current.status !== "playing" || current.current.length >= size) return prev;
      return {
        ...prev,
        [size]: { ...current, current: `${current.current}${digit}`, error: "" },
      };
    });
  }, []);

  const deleteDigit = useCallback(() => {
    const size = gridRef.current;
    if (busyRef.current || sheetRef.current) return;
    setBoards((prev) => {
      const current = prev[size];
      if (!current || current.status !== "playing" || !current.current) return prev;
      return {
        ...prev,
        [size]: { ...current, current: current.current.slice(0, -1), error: "" },
      };
    });
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.key === "Enter") {
        event.preventDefault();
        submitCurrent();
        return;
      }
      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        deleteDigit();
        return;
      }
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        typeDigit(event.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteDigit, submitCurrent, typeDigit]);

  const other = grid === 4 ? 5 : 4;
  const otherBoard = boards[other];
  const bothDone = boards[4].status !== "playing" && boards[5].status !== "playing";
  const copy = cardCopy(creator?.displayName, creatorScore, grid);
  const sheetBoard = sheet ? boards[sheet.grid] : null;
  const sheetPoints = sheet ? pointsForResult(sheet.solved, sheet.guessCount) : 0;
  const leaderboardCreator = creator?.id || (searchParams.get("creator") || "").trim();
  const leaderboard = `/games/number-drop/leaderboard?creator=${encodeURIComponent(leaderboardCreator)}&grid=${grid}&date=${date}`;

  const filledRows = [
    ...board.rows.map((row) => ({ letters: String(row.guess || "").split(""), tiles: row.tiles || [], locked: true })),
  ];
  if (board.status === "playing" && filledRows.length < 6) {
    filledRows.push({
      letters: board.current.split(""),
      tiles: [],
      locked: false,
    });
  }
  while (filledRows.length < 6) filledRows.push({ letters: [], tiles: [], locked: false });

  async function onShare() {
    if (!sheetBoard) return;
    const text = shareText(puzzleNo, sheet.grid, sheetBoard.rows);
    setShareNote("");
    const blob = await shareImage(sheetBoard.rows);
    let shared = false;
    try {
      const file = blob ? new File([blob], "number-drop.png", { type: "image/png" }) : null;
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ text, files: [file] });
        shared = true;
      } else if (navigator.share) {
        await navigator.share({ text });
        shared = true;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
    let copied = false;
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch (_) {}
    if (blob) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "number-drop.png";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
    if (shared) setShareNote("Shared.");
    else if (copied) setShareNote("Copied the color grid and downloaded the image.");
    else if (blob) setShareNote("Downloaded the color grid.");
    else setShareNote("Share didn't go through.");
  }

  return (
    <GameWithClosingNow>
    <div className="nd-page">
      {gameSwitch}
      <header className="nd-header">
        <div className="nd-mark">100VO</div>
        <div className="nd-puzzle">NUMBER DROP · No. {puzzleNo}</div>
        <Link className="nd-boards" to={leaderboard}>Boards</Link>
      </header>

      <div className="nd-segs" role="tablist" aria-label="Number length">
        {[4, 5].map((size) => {
          const status = boards[size].status;
          return (
            <button
              key={size}
              type="button"
              role="tab"
              aria-selected={grid === size}
              className={`nd-seg${grid === size ? " active" : ""}`}
              onClick={() => {
                setGrid(size);
                setSheet(null);
              }}
            >
              <span className={`nd-dot${status === "playing" ? "" : ` nd-dot--${status}`}`} />
              {size} digits
            </button>
          );
        })}
      </div>

      <Link className="nd-creator" to={leaderboard}>
        <div className="nd-avatar" aria-hidden="true">{initials(creator?.displayName)}</div>
        <div className="nd-creator-copy">
          <div className="nd-creator-title">{creator ? copy.title : "Number Drop"}</div>
          <div className="nd-creator-sub">{creatorNote || copy.subtitle}</div>
        </div>
        {copy.rows.length > 0 && (
          <div className="nd-mini" aria-hidden="true">
            {copy.rows.map((row, rowIndex) => (
              <div className="nd-mini-row" key={`mini-${rowIndex}`}>
                {row.map((tile, index) => (
                  <span className={`nd-mini-tile ${tile === "unknown" ? "" : tile}`} key={`${rowIndex}-${index}`} />
                ))}
              </div>
            ))}
          </div>
        )}
      </Link>

      {board.digitSum != null && (
        <div className="nd-hint">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7A4B03" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <div className="nd-hint-text">These digits add up to {board.digitSum}</div>
        </div>
      )}

      <div className="nd-grid" aria-label={`${grid} digit guesses`}>
        {filledRows.map((row, rowIndex) => {
          const isCurrent = board.status === "playing" && rowIndex === board.rows.length;
          return (
            <div
              className={`nd-row${isCurrent && shake ? " is-shaking" : ""}`}
              key={`row-${grid}-${rowIndex}`}
              onAnimationEnd={() => setShake(false)}
            >
              {Array.from({ length: grid }, (_, index) => {
                const letter = row.letters[index] || "";
                const tile = row.locked ? (row.tiles[index] || "") : "";
                const typed = !row.locked && letter;
                return (
                  <div
                    key={`${grid}-${rowIndex}-${index}`}
                    className={`nd-tile${tile ? ` ${tile}` : ""}${typed ? " typed" : ""}`}
                  >
                    {letter}
                  </div>
                );
              })}
            </div>
          );
        })}
        <div className="nd-caption">
          {grid === 5
            ? "Five digits, no repeats, 6 guesses."
            : "Four digits, no repeats, 6 guesses."}
        </div>
      </div>
      {board.error && <div className="nd-error" role="alert">{board.error}</div>}

      <div className="nd-keys">
        <div className="nd-key-row">
          {DIGITS.map((digit) => (
            <button
              type="button"
              key={digit}
              className="nd-key"
              onClick={() => typeDigit(digit)}
              disabled={busy || board.status !== "playing"}
            >
              {digit}
            </button>
          ))}
        </div>
        <div className="nd-key-actions">
          <button type="button" className="nd-key wide" onClick={deleteDigit} disabled={busy}>Delete</button>
          <button type="button" className="nd-key wide enter" onClick={submitCurrent} disabled={busy}>Enter</button>
        </div>
      </div>

      {sheet && sheetBoard && (
        <div className="nd-sheet-backdrop" onClick={() => setSheet(null)}>
          <div
            className="nd-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="nd-sheet-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="nd-sheet-eyebrow">{sheet.solved ? "Solved" : "No more guesses"}</p>
            <h2 id="nd-sheet-title" className="nd-sheet-headline">
              {sheet.solved ? "You got it" : "That's the round"}
            </h2>
            <p className="nd-sheet-points">
              {sheetPoints} {sheetPoints === 1 ? "point" : "points"}
            </p>
            <p className="nd-sheet-compare">
              {comparisonLine(creator?.displayName, creatorScore, sheet.grid, sheet.solved, sheet.guessCount)}
            </p>
            <div className="nd-sheet-grid" aria-hidden="true">
              {sheetBoard.rows.map((row, rowIndex) => (
                <div className="nd-mini-row" key={`sheet-${rowIndex}`}>
                  {(row.tiles || []).map((tile, index) => (
                    <span className={`nd-mini-tile ${tile}`} key={`${rowIndex}-${index}`} />
                  ))}
                </div>
              ))}
            </div>
            <div className="nd-sheet-actions">
              {otherBoard.status === "playing" && (
                <button
                  type="button"
                  className="nd-sheet-primary"
                  onClick={() => {
                    setGrid(other);
                    setSheet(null);
                  }}
                >
                  Play the {other}-digit
                </button>
              )}
              {bothDone && (
                <>
                  <Link className="nd-sheet-primary" to={leaderboard}>See the board</Link>
                  <button type="button" className="nd-sheet-secondary" onClick={onShare}>Share</button>
                </>
              )}
            </div>
            {shareNote && <p className="nd-share-note">{shareNote}</p>}
          </div>
        </div>
      )}
    </div>
    </GameWithClosingNow>
  );
}
